import {
  getUnmatchedItemRefs,
  hasActiveIssueRowBalance,
  rowMatchesUnmatchedItem,
} from './zoneAiSuggestionsUtils';

const getFieldValue = (row, field) => {
  const value = typeof field === 'function' ? field(row) : row?.[field];
  if (value == null || value === '') {
    return '';
  }
  return value;
};

const parseQty = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** 0 = active balance, 1 = returned, 2 = sold, 3 = other */
export const getIssueRowStatusTier = (row) => {
  const balance = parseQty(row?.balance);
  const returnQty = parseQty(row?.return);
  const sold = parseQty(row?.sold);

  if (balance > 0) {
    return 0;
  }
  if (returnQty > 0) {
    return 1;
  }
  if (sold > 0) {
    return 2;
  }
  return 3;
};

export const sortIssueRowsByStatus = (rows) => {
  if (!rows?.length) {
    return rows || [];
  }

  return [...rows]
    .map((row, index) => ({ row, index }))
    .sort((entryA, entryB) => {
      const tierDiff =
        getIssueRowStatusTier(entryA.row) - getIssueRowStatusTier(entryB.row);
      if (tierDiff !== 0) {
        return tierDiff;
      }
      return entryA.index - entryB.index;
    })
    .map(({ row }) => row);
};

const sortIssueRowsInternal = (rows, sortConfig) => {
  const statusSorted = sortIssueRowsByStatus(rows);

  if (!sortConfig?.key) {
    return statusSorted;
  }

  const tiers = [0, 1, 2, 3];
  return tiers.flatMap((tier) =>
    sortTableRows(
      statusSorted.filter((row) => getIssueRowStatusTier(row) === tier),
      sortConfig,
      ZONE_ISSUE_SORT_FIELDS
    )
  );
};

export const isIssueRowNewAdd = (row) => Boolean(row?.new_add);

export const prioritizeIssueRows = (
  rows = [],
  { unmatchedPoProductIds = [], aiSuggestions = null } = {}
) => {
  if (!rows?.length) {
    return rows || [];
  }

  const unmatchedRefs = aiSuggestions ? getUnmatchedItemRefs(aiSuggestions) : [];
  const unmatchedIdSet = new Set(
    (unmatchedPoProductIds || []).map((id) => String(id)).filter(Boolean)
  );

  const isPrioritizedUnmatchedRow = (row) => {
    if (!hasActiveIssueRowBalance(row)) {
      return false;
    }

    if (unmatchedRefs.length) {
      return unmatchedRefs.some((ref) => rowMatchesUnmatchedItem(row, ref));
    }

    const rowIds = [row?.id, row?.po_product_id, row?.product_id]
      .filter((value) => value != null && value !== '')
      .map((value) => String(value));

    return rowIds.some((id) => unmatchedIdSet.has(id));
  };

  const newAdd = [];
  const unmatched = [];
  const rest = [];

  rows.forEach((row) => {
    if (isIssueRowNewAdd(row)) {
      newAdd.push(row);
      return;
    }

    if (isPrioritizedUnmatchedRow(row)) {
      unmatched.push(row);
      return;
    }

    rest.push(row);
  });

  if (!newAdd.length && !unmatched.length) {
    return rows;
  }

  return [...newAdd, ...unmatched, ...rest];
};

export const sortTableRows = (rows, sortConfig, fieldMap) => {
  if (!sortConfig?.key || !rows?.length) {
    return rows || [];
  }

  const field = fieldMap[sortConfig.key];
  if (!field) {
    return rows;
  }

  const direction = sortConfig.dir === 'desc' ? 'desc' : 'asc';
  const isNumericField =
    sortConfig.key === 'QTY' ||
    sortConfig.key === 'Issue' ||
    sortConfig.key === 'Sold' ||
    sortConfig.key === 'Return' ||
    sortConfig.key === 'Balance';

  return [...rows].sort((rowA, rowB) => {
    const valueA = getFieldValue(rowA, field);
    const valueB = getFieldValue(rowB, field);

    if (isNumericField) {
      const numA = Number(valueA) || 0;
      const numB = Number(valueB) || 0;
      return direction === 'asc' ? numA - numB : numB - numA;
    }

    const comparison = String(valueA).localeCompare(String(valueB), undefined, {
      sensitivity: 'base',
      numeric: true,
    });

    return direction === 'asc' ? comparison : -comparison;
  });
};

export const ADD_INVENTORY_SORT_FIELDS = {
  SKU: 'SKU',
  Item: 'Item_name',
  Size: 'Size_name',
  'Stock ID': 'stock_id',
  QTY: 'po_QTY',
  Status: 'status_type',
};

export const ZONE_ISSUE_SORT_FIELDS = {
  SKU: 'SKU',
  Item: 'item_name',
  Collection: (row) => row?.collection_name || row?.collection || '',
  Size: (row) => row?.size || row?.size_name || '',
  'Stock ID': 'stock_id',
};

export const sortInventoryRows = (rows, sortConfig) =>
  sortTableRows(rows, sortConfig, ADD_INVENTORY_SORT_FIELDS);

export const sortIssueRows = (rows, sortConfig, options = {}) => {
  const sorted = sortIssueRowsInternal(rows, sortConfig);
  return prioritizeIssueRows(sorted, {
    unmatchedPoProductIds: options?.unmatchedPoProductIds,
    aiSuggestions: options?.aiSuggestions,
  });
};
